import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  formulatorEventSchema,
  type FormulatorEngineErrorCode,
  type FormulatorEvent,
  type FormulatorRunPhase,
  type FormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { workItemInterviewKeys } from "@/hooks/queries/use-work-item-interview";
import { useToolboxBridge } from "./bridge";

/**
 * The Formulator's half of the SpecTree Toolbox bridge, for the Toolbox
 * Formulator page (epic PI-SPEC-SW-E282, feature PI-SPEC-SW-1418).
 *
 * The interview runs on the person's own Claude Code seat inside SpecTree
 * Toolbox: the page starts a session and sends each reply through
 * `window.spectreeToolbox.formulator`, and each turn reports through
 * `formulator.onEvent`. The session itself (messages, the live Draft, what
 * Claude found) is server state the page reads from the SpecTree API with
 * TanStack Query; the bridge is client state. When a turn finishes the page
 * refetches the session.
 *
 * The bridge and event types come from `@ttc-spectree/toolbox-contract`;
 * never re-type them here.
 */

export type FormulatorTurnFinishedEvent = Extract<FormulatorEvent, { type: "turn-finished" }>;
export type FormulatorTurnProgressEvent = Extract<FormulatorEvent, { type: "turn-progress" }>;

/** The activity line shown while Claude works, when the engine has not sent one yet. */
export const DEFAULT_FORMULATOR_ACTIVITY = "Checking the code";

/** Plain activity per run phase, used when a `turn-progress` event carries no activity line. */
export const FORMULATOR_PHASE_ACTIVITY: Record<FormulatorRunPhase, string> = {
  "preparing-repos": "Updating the code on this computer",
  "reading-code": DEFAULT_FORMULATOR_ACTIVITY,
  "checking-duplicates": "Looking for items that already describe this",
  thinking: "Thinking about the next question",
};

/** The formulator half of the bridge when the page runs inside SpecTree Toolbox and the app ships it, else null. */
export function useFormulatorBridge(): FormulatorToolsetBridge | null {
  const bridge = useToolboxBridge();
  return bridge?.formulator ?? null;
}

/** What the page knows about the session's current turn from the engine's events. */
export interface FormulatorTurnState {
  /** True between the turn's first `turn-progress` event and its `turn-finished` event. */
  running: boolean;
  /** The latest plain activity line of the running turn, or null when none is running. */
  activity: string | null;
  /** How the last turn of this session ended, or null when none has ended while the page was open. */
  lastFinished: FormulatorTurnFinishedEvent | null;
}

const IDLE: FormulatorTurnState = { running: false, activity: null, lastFinished: null };

function activityOf(event: FormulatorTurnProgressEvent): string {
  const line = event.activity?.trim();
  return line !== undefined && line !== "" ? line : FORMULATOR_PHASE_ACTIVITY[event.phase];
}

/**
 * Subscribe to the formulator's events for one session while mounted.
 *
 * - `turn-progress` for the session: the turn is running; the latest
 *   activity line replaces the previous one. With no session yet
 *   (`sessionId` null), progress events without a session (the repository
 *   copies being prepared before the session exists) count too.
 * - `turn-finished` for the session: the turn ended. The session, the
 *   resume list and any cached review are refetched, so the page shows the
 *   next question, quick picks, Draft and What Claude found from the API. A
 *   review on screen is refetched in place (a draft or redraft turn, epic
 *   PI-SPEC-SW-E321); one not on screen is dropped.
 *
 * Events cross the app/page version boundary, so each is parsed with the
 * contract's `formulatorEventSchema` and one the page cannot read is dropped.
 * `onFinished` is called after the refetch is requested.
 */
export function useFormulatorEvents(
  sessionId: string | null,
  options: { onFinished?: (event: FormulatorTurnFinishedEvent) => void } = {}
): FormulatorTurnState {
  const formulator = useFormulatorBridge();
  const queryClient = useQueryClient();
  const [state, setState] = useState<FormulatorTurnState>(IDLE);
  const onFinished = useRef(options.onFinished);
  useEffect(() => {
    onFinished.current = options.onFinished;
  }, [options.onFinished]);

  // A different session starts from a clean slate.
  useEffect(() => {
    setState(IDLE);
  }, [sessionId]);

  useEffect(() => {
    if (!formulator) return undefined;
    return formulator.onEvent((raw: unknown) => {
      const parsed = formulatorEventSchema.safeParse(raw);
      if (!parsed.success) return;
      const event = parsed.data;
      if (event.type === "turn-progress") {
        const mine = sessionId === null ? event.sessionId === undefined : event.sessionId === sessionId;
        if (!mine) return;
        setState((current) => ({ ...current, running: true, activity: activityOf(event) }));
        return;
      }
      if (event.type === "turn-finished") {
        if (sessionId === null || event.sessionId !== sessionId) return;
        setState({ running: false, activity: null, lastFinished: event });
        void queryClient.invalidateQueries({ queryKey: workItemInterviewKeys.detail(sessionId) });
        void queryClient.invalidateQueries({ queryKey: workItemInterviewKeys.active() });
        // A review on screen (a draft or redraft turn finished while the person
        // reviews) is refetched in place; one not on screen is dropped, so the
        // next review never opens on a stale copy.
        const reviewKey = workItemInterviewKeys.review(sessionId);
        const review = queryClient.getQueryCache().find({ queryKey: reviewKey, exact: true });
        if (review && review.getObserversCount() > 0) void queryClient.invalidateQueries({ queryKey: reviewKey, exact: true });
        else queryClient.removeQueries({ queryKey: reviewKey, exact: true });
        onFinished.current?.(event);
      }
    });
  }, [formulator, queryClient, sessionId]);

  return state;
}

/** A plain message for an engine error, and whether the Setup checks page can fix it. */
export interface FormulatorErrorMessage {
  text: string;
  /** True when `/toolbox/setup` is where the person fixes it (Claude, plugin, MCP, git sign-in). */
  setup: boolean;
  /** True when sending the same answer again is the fix; the notice shows Try again. */
  retry?: boolean;
}

/**
 * NO_TURN_RETURNED after the person answered: Claude ran but did not finish
 * its next question. The answer was not posted, so the page keeps it and
 * offers Try again (UX-PM-23). Never plugin or Setup checks wording.
 */
export const NO_TURN_REPLY_TEXT = "Claude did not finish its next question. Your answer is kept. Try again.";

/** NO_TURN_RETURNED on the opening turn, where the person has typed no answer yet. */
export const NO_TURN_OPENING_TEXT = "Claude did not finish its first question. Try again.";

const ERROR_MESSAGES: Record<FormulatorEngineErrorCode, FormulatorErrorMessage> = {
  CLAUDE_NOT_INSTALLED: { text: "Claude Code is not installed on this computer.", setup: true },
  CLAUDE_NOT_SIGNED_IN: { text: "Claude Code is not signed in on this computer. Sign in, then try again.", setup: true },
  PLUGIN_MISSING: { text: "The SpecTree plugin for Claude Code is missing on this computer.", setup: true },
  MCP_NOT_CONNECTED: { text: "Claude Code cannot reach SpecTree's tools on this computer.", setup: true },
  REPO_NOT_MAPPED: {
    text: "This project has no repository mapped yet, so Claude has no code to read. A Project Manager or an Admin adds one on the Project page's Repositories tab.",
    setup: false,
  },
  REPO_AUTH_REQUIRED: { text: "Git needs you to sign in before it can copy a mapped repository.", setup: true },
  REPO_CLONE_FAILED: { text: "A mapped repository could not be copied to this computer.", setup: true },
  USAGE_LIMIT: {
    text: "Your Claude usage limit was reached. Nothing is lost: try again once it resets.",
    setup: false,
  },
  TURN_TIMEOUT: { text: "Claude took too long to answer. Nothing was saved; send it again.", setup: false },
  NO_TURN_RETURNED: { text: NO_TURN_REPLY_TEXT, setup: false, retry: true },
  SESSION_NOT_FOUND: { text: "This interview no longer exists. Start a new one.", setup: false },
  BUSY: {
    text: "SpecTree Toolbox is already running Claude. Wait for it to finish, then try again.",
    setup: false,
  },
  NETWORK: { text: "SpecTree Toolbox could not reach SpecTree. Check your connection and try again.", setup: false },
  UNKNOWN: { text: "Something went wrong in SpecTree Toolbox.", setup: false },
};

/** Codes whose engine message names the repository involved, so it is shown too. */
const NAMES_THE_REPO: ReadonlySet<FormulatorEngineErrorCode> = new Set(["REPO_AUTH_REQUIRED", "REPO_CLONE_FAILED"]);

/**
 * Plain words for an engine error code. The engine's own message is added
 * where it names the repository, and for `UNKNOWN`. Pure; exported for tests.
 */
export function formulatorErrorMessage(
  code: FormulatorEngineErrorCode | undefined,
  engineMessage?: string
): FormulatorErrorMessage {
  const base = ERROR_MESSAGES[code ?? "UNKNOWN"];
  const detail = engineMessage?.trim();
  if (detail && (code === undefined || code === "UNKNOWN" || NAMES_THE_REPO.has(code))) {
    return { ...base, text: `${base.text} ${detail}` };
  }
  return base;
}

/**
 * The message for a turn Claude owes before any answer was typed (the
 * opening turn): NO_TURN_RETURNED says "first question" and drops "Your
 * answer is kept". Every other message is returned unchanged. Pure; exported
 * for tests.
 */
export function formulatorOpeningTurnMessage(message: FormulatorErrorMessage): FormulatorErrorMessage {
  return message.text === NO_TURN_REPLY_TEXT ? { ...message, text: NO_TURN_OPENING_TEXT } : message;
}

/** True when the app offers Redraft (contract formulator 3, epic PI-SPEC-SW-E321); an older Toolbox has no `redraft`. */
export function canRedraft(
  formulator: FormulatorToolsetBridge | null | undefined
): formulator is FormulatorToolsetBridge & Required<Pick<FormulatorToolsetBridge, "redraft">> {
  return typeof formulator?.redraft === "function";
}

/** NO_TURN_RETURNED on a redraft turn: Claude ran but did not finish the new text. */
export const NO_TURN_REDRAFT_TEXT = "Claude did not finish the new text. What you asked for is kept. Try again.";

/**
 * The message for a failed redraft: NO_TURN_RETURNED speaks of the new text
 * and the kept instruction, never of a question or an answer. Every other
 * message is returned unchanged. Pure; exported for tests.
 */
export function formulatorRedraftMessage(message: FormulatorErrorMessage): FormulatorErrorMessage {
  return message.text === NO_TURN_REPLY_TEXT ? { ...message, text: NO_TURN_REDRAFT_TEXT } : message;
}
